import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router';
import { observer } from 'mobx-react-lite';
import { initializePaddle } from '@paddle/paddle-js';
import { addMonths, isPast } from 'date-fns';

import { PageLoader } from './components/page-loader';
import { Space, Upload } from './entities';
import { api } from '../api';
import { DesktopWarning } from './components/desktop-warning';
import { authStore } from '../core/stores/auth-store';
import { UnauthorizedDialog } from '../components/dialogs/unauthorized-dialog';
import { AppHeader } from './components/app-header';
import { bulkUpdateStore } from '../core/stores/bulk-update';
import { ShotsBulkUpdateModal } from '../components/dialogs/shots-bulk-update.modal';
import { globalConfig } from '../configuration/config';
import { PastDueSubscriptionBanner } from './components/past-due-subscription-banner';
import { PastDueWarningModal } from '../features/billing/components/past-due-warning.modal';
import { Drawer } from './components/Drawer/drawer';
import { useCurrentMember } from '../hooks/use-current-member';
import { context } from './context';
import { appSocket } from '../app-socket';
import type { SpaceOutletContext } from './space-outlet';

import styles from './core.module.scss';
type PageParams = {
  spaceId: string;
};

export const Core: React.FC = observer(() => {
  const { spaceId } = useParams() as PageParams;
  const space = Space.getOne(spaceId)!;
  const [isPastDueModalOpen, setIsPastDueModalOpen] = useState(false);
  const [isSpaceLocked, setIsSpaceLocked] = useState(false);
  const currentMember = useCurrentMember();
  const navigate = useNavigate();

  useEffect(() => {
    api.updateSpaceInHeaders(spaceId);
    context.space = Space.getOne(spaceId)!;
    context.navigate = navigate;
  }, [spaceId]);

  useEffect(() => {
    if (!globalConfig.config.paddleToken) {
      return;
    }

    initializePaddle({
      token: globalConfig.config.paddleToken,
      pwCustomer: {
        id: space.subscription?.customerId || undefined,
      },
    });
  }, [space, spaceId]);

  /**
   * Connect to live update from socket-io
   */
  useEffect(() => {
    appSocket.init(spaceId);
  }, [spaceId]);

  /**
   * TODO URGENT
   * Will not work when reopening the page as the asset will not be there anymore
   */

  useEffect(() => {
    Upload.loadDB();
  }, []);

  /**
   * Check if Space is overdue and display modal id overdue for more than one month
   */
  useEffect(() => {
    if (
      space.isSubscriptionPastDue &&
      !!space.subscription?.past_due_at &&
      (currentMember?.role || 'guest')
    ) {
      if (!space.subscription || !space.subscription.past_due_at) return;

      if (isPast(addMonths(space.subscription.past_due_at, 2))) {
        setIsSpaceLocked(true);
      } else if (isPast(addMonths(space.subscription.past_due_at, 1))) {
        setIsPastDueModalOpen(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [space, spaceId]);

  if (!spaceId || !space) {
    return <PageLoader />;
  }

  const displayBanner = !!space?.isSubscriptionPastDue;

  return (
    <div className={styles['container']}>
      {displayBanner && <PastDueSubscriptionBanner spaceId={spaceId} />}

      <div className={styles['app']} data-has-banner={displayBanner}>
        <Drawer />

        <div className={styles['main']}>
          <AppHeader space={space} hasBanner={displayBanner} />

          <div className={styles['content']}>
            <div className={styles['content-container']}>
              <Outlet context={{ space, isSpaceLocked } satisfies SpaceOutletContext} />
            </div>
          </div>
        </div>
      </div>

      {bulkUpdateStore.shouldOpenModal && (
        <ShotsBulkUpdateModal
          shotsCount={bulkUpdateStore.shotsCount}
          onUpdate={bulkUpdateStore.onUpdate}
          onCancel={bulkUpdateStore.onCancel}
        />
      )}

      {isPastDueModalOpen && (
        <PastDueWarningModal spaceId={spaceId} onCancel={() => setIsPastDueModalOpen(false)} />
      )}

      {!authStore.isAuthorized && <UnauthorizedDialog />}

      <DesktopWarning />
    </div>
  );
});
